import { useState, useEffect } from 'react';
import type { HowManySheetProps } from '../types';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ScrollableNumbers } from '@/components/ui/scrollable-numbers';
import { InfoWarningCard } from '@/components/shared/events/detail/InfoWarningCard';
import { Icon } from '@/components/ui/icon';
import { useClientTranslation } from '@/lib/core';

export function HowManySheet({
  isOpen,
  onClose,
  cmsProps,
  booking,
  onBookingChange,
  infoMessage,
}: HowManySheetProps) {
  const t = useClientTranslation();
  const [highlighted, setHighlighted] = useState<number>(booking.guestCount ?? 4);
  const [maxGuests, setMaxGuests] = useState<number>(20);

  useEffect(() => {
    if (!isOpen) return;
    const max = booking.venue?.guestsMax ?? 20;
    setMaxGuests(max);
    setHighlighted(Math.min(booking.guestCount ?? 4, max));
  }, [isOpen, booking.guestCount, booking.venue?.guestsMax]);

  const title = cmsProps?.title ?? t('App.Labels.HowMany');
  const subtitle = cmsProps?.subtitle ?? t('App.Labels.ChooseHowMany');

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        <DrawerHeader className="shrink-0 gap-2 pt-8">
          <div className="flex items-center gap-8">
            <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">{title}</DrawerTitle>
            <DrawerClose
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label="Close"
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </DrawerClose>
          </div>
          <DrawerDescription className="text-[16px] text-[#707070]">{subtitle}</DrawerDescription>
        </DrawerHeader>
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
          <div className="flex h-full flex-col gap-8 px-4 pt-4 pb-10.5">
            {infoMessage && <InfoWarningCard lines={[{ icon: 'info-circle', text: infoMessage }]} />}
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <ScrollableNumbers value={highlighted} onChange={setHighlighted} max={maxGuests} />
            </div>
            {/* SELECT button — always at the sheet bottom */}
            <div className="shrink-0">
              <Button
                variant="primary-solid"
                palette="dark"
                size="lg"
                className="w-full"
                onClick={() => {
                  onBookingChange({ guestCount: highlighted });
                  onClose();
                }}
              >
                {cmsProps?.selectButtonLabel ?? t('App.Labels.Select')}
              </Button>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
